import React, { useState } from "react";
import {
  Button,
  Modal,
  Alert,
  Table,
  Tag,
  Space,
  notification
} from "antd";
import {
  SendOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { useCustomMutation } from "@refinedev/core";
import { ConflictInfo, CHECK_PROCUREMENT_PLAN_CONFLICTS } from "../queries";
import WorkflowDiagram from "../../purchase-template/components/WorkflowDiagram";
import { useSubmitProcurementPlan } from "../hooks/useSubmitProcurementPlan";

// Component Props Interface
export interface SubmitButtonProps {
  planId?: string;
  planData?: any;
  plan?: any;
  buttonText?: string;
  buttonType?: "default" | "primary";
  icon?: React.ReactNode;
  onSuccess?: () => void;
  disabled?: boolean;
  size?: "small" | "middle" | "large";
}

export const SubmitButton: React.FC<SubmitButtonProps> = ({
  planId,
  planData,
  plan,
  buttonText = "Submit",
  buttonType = "primary",
  icon = <SendOutlined />,
  onSuccess,
  disabled = false,
  size = "middle"
}) => {
  const [conflictInfo, setConflictInfo] = useState<ConflictInfo | null>(null);
  const [isConflictModalVisible, setIsConflictModalVisible] = useState(false);
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false);

  // Mutation for checking conflicts
  const { mutateAsync: checkConflictsMutation, isLoading: checkingConflicts } = useCustomMutation();

  // Submit mutation hook using custom hook
  const { submit: submitProcurementPlan, isLoading: submitLoading } = useSubmitProcurementPlan({
    onSuccess: () => {
      // Call the parent's onSuccess callback if provided
      onSuccess?.();
    },
    onConflict: (conflictInfo) => {
      // Handle conflict scenario
      setConflictInfo(conflictInfo);
      setIsConflictModalVisible(true);
    },
    onError: (error) => {
      console.error('Submit error:', error);
    }
  });

  // Handle form submission
  const handleSubmit = async () => {
    try {
      // First, check for conflicts
      const currentPlan = plan || (planData ? planData : null);
      if (!currentPlan && !planId) {
        console.error('Either planId, planData, or plan must be provided');
        notification.error({
          message: 'Submission Error',
          description: 'Invalid submission parameters. Please try again.',
        });
        return;
      }

      const teamId = currentPlan?.teamId;
      const fiscalYear = currentPlan?.fiscalYear;
      const planningPeriod = currentPlan?.planningPeriod;
      const excludeId = planId || currentPlan?.id;

      if (!teamId || !fiscalYear || !planningPeriod) {
        console.error('Missing required fields for conflict check');
        notification.error({
          message: 'Submission Error',
          description: 'Missing required plan information. Please try again.',
        });
        return;
      }

      const result = await checkConflictsMutation({
        url: "",
        method: "post",
        values: {
          teamId,
          fiscalYear: parseFloat(fiscalYear),
          planningPeriod,
          excludeId
        },
        meta: {
          gqlQuery: CHECK_PROCUREMENT_PLAN_CONFLICTS,
        },
      });

      const conflictResult = (result as any)?.data?.checkProcurementPlanConflicts;

      if (conflictResult?.hasConflict) {
        // Show conflict modal
        setConflictInfo(conflictResult);
        setIsConflictModalVisible(true);
      } else {
        // No conflicts, show confirmation modal
        setIsConfirmationModalVisible(true);
      }
    } catch (error) {
      console.error('Conflict check failed:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to check for conflicts. Please try again.',
      });
    }
  };

  // Handle conflict resolution - proceed with saving as draft
  const handleConflictResolution = () => {
    setIsConflictModalVisible(false);
    notification.info({
      message: 'Plan Saved as Draft',
      description: 'The plan has been saved as draft due to conflicts. You can resolve conflicts and submit again later.',
    });
    setConflictInfo(null);
    // Call onSuccess callback since the plan was saved (even as draft)
    onSuccess?.();
  };

  // Handle conflict modal cancel
  const handleConflictCancel = () => {
    setIsConflictModalVisible(false);
    setConflictInfo(null);
  };

  // Handle confirmation modal OK
  const handleConfirmationOk = async () => {
    setIsConfirmationModalVisible(false);
    try {
      // Proceed with actual submission
      if (planId) {
        await submitProcurementPlan({
          planId: planId,
        });
      } else if (planData) {
        await submitProcurementPlan({
          planData: planData,
        });
      }
    } catch (error) {
      console.error('Submission failed:', error);
    }
  };

  // Handle confirmation modal cancel
  const handleConfirmationCancel = () => {
    setIsConfirmationModalVisible(false);
  };

  // Columns for the conflicting plans table
  const conflictTableColumns = [
    {
      title: 'Plan Number',
      dataIndex: 'planNumber',
      key: 'planNumber',
    },
    {
      title: 'Team',
      dataIndex: 'teamName',
      key: 'teamName',
      render: (teamName: string, record: any) => (
        <span>{teamName || record.teamId || 'Unknown Team'}</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'APPROVED' ? 'green' : status === 'SUBMITTED' ? 'blue' : 'orange'}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Fiscal Year',
      dataIndex: 'fiscalYear',
      key: 'fiscalYear',
    },
    {
      title: 'Planning Period',
      dataIndex: 'planningPeriod',
      key: 'planningPeriod',
    },
  ];

  return (
    <>
      {/* Submit Button */}
      <Button
        type={buttonType}
        onClick={handleSubmit}
        icon={icon}
        loading={submitLoading || checkingConflicts}
        disabled={disabled}
        size={size}
      >
        {buttonText}
      </Button>

      {/* Conflict Resolution Modal */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            Submission Conflicts Detected
          </Space>
        }
        open={isConflictModalVisible}
        onOk={handleConflictResolution}
        onCancel={handleConflictCancel}
        okText="Save as Draft"
        cancelText="Cancel"
        width={700}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            message="Conflicts Found"
            description={
              conflictInfo?.message || 
              "There are conflicts with existing procurement plans that prevent submission."
            }
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        </div>

        {conflictInfo?.conflictingPlans && conflictInfo.conflictingPlans.length > 0 && (
          <div>
            <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Conflicting Plans:</h4>
            <Table
              dataSource={conflictInfo.conflictingPlans}
              columns={conflictTableColumns}
              pagination={false}
              size="small"
              rowKey="id"
              style={{ marginBottom: 16 }}
              scroll={{ x: 'max-content' }}
            />
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <p style={{ marginBottom: 8, fontWeight: 500 }}>
            What would you like to do?
          </p>
          <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
            <li style={{ marginBottom: 4 }}>
              Click <strong>"Save as Draft"</strong> to save your changes without submitting
            </li>
            <li style={{ marginBottom: 4 }}>
              Click <strong>"Cancel"</strong> to return and make modifications
            </li>
            <li style={{ marginBottom: 0 }}>
              Resolve the conflicts and try submitting again later
            </li>
          </ul>
        </div>
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        title={
          <Space>
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            Confirm Submission
          </Space>
        }
        open={isConfirmationModalVisible}
        onOk={handleConfirmationOk}
        onCancel={handleConfirmationCancel}
        okText="Submit"
        cancelText="Cancel"
        width={800}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            message="No Conflicts Found"
            description="Your procurement plan can be submitted successfully."
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
        </div>

        {/* Flow Diagram */}
        {(() => {
          // Fallback logic: use plan.externalWorkflow, then planData.externalWorkflow, or template workflow
          const externalWorkflow = plan?.externalWorkflow || planData?.externalWorkflow;
          const approvalType = plan?.approvalType || planData?.approvalType;
          
          return approvalType === 'EXTERNAL' && externalWorkflow ? (
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Approval Flow:</h4>
              <WorkflowDiagram flow={externalWorkflow} />
            </div>
          ) : null;
        })()}

        {plan?.approvalType === 'LOCAL' && (
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Approval Flow:</h4>
            <Alert
              message="Local Approval Process"
              description="This plan will follow the standard local approval workflow."
              type="info"
              showIcon
            />
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <p style={{ marginBottom: 8, fontWeight: 500 }}>
            By clicking "Submit", you confirm that:
          </p>
          <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
            <li style={{ marginBottom: 4 }}>
              The first approver will be notified immediately
            </li>
            <li style={{ marginBottom: 4 }}>
              The procurement plan will be marked as submitted
            </li>
            <li style={{ marginBottom: 0 }}>
              You will no longer be able to edit this plan
            </li>
          </ul>
        </div>
      </Modal>
    </>
  );
};

export default SubmitButton;